import { useState } from 'react';
import { AIRTIME_MIN, AIRTIME_MAX, AIRTIME_PRESETS } from '@arcnet/contracts';
import type { Checkout, Payment } from '@arcnet/contracts';
import {
  usePayments, useBuyAirtime, useBalanceFollowsPayments, isAwaiting,
} from '../../lib/api/hooks-payments.js';
import { useSubroute } from '../../lib/subroute.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { formatMoney, formatDateTime } from '../../lib/format.js';
import { ReceiptView } from './Receipt.js';

/**
 * Sends the browser to PayFast's hosted page with the signed form. The card is
 * entered there, never here (docs/28 §10).
 */
export function toPayfast(checkout: Checkout): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = checkout.action;
  for (const [name, value] of checkout.fields) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

const AMOUNT = /^\d{1,5}(\.\d{1,2})?$/;

export function BuyAirtime({ tenantId }: { tenantId: string }) {
  const buy = useBuyAirtime(tenantId);
  const [amount, setAmount] = useState('250');
  const n = Number(amount);
  const valid = AMOUNT.test(amount) && n >= AIRTIME_MIN && n <= AIRTIME_MAX;
  // While PayFast's page loads, a second click would start a second payment.
  const leaving = buy.isPending || buy.isSuccess;

  return (
    <form
      style={{ marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid var(--arc-border)' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !leaving) buy.mutate(amount, { onSuccess: toPayfast });
      }}
    >
      <div className="arc-row" style={{ gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <span className="arc-label">Amount</span>
          <Segmented label="Amount" value={amount} onChange={setAmount}
            options={AIRTIME_PRESETS.map((v) => [v, `R${v}`] as const)} />
        </div>
        <div className="arc-field" style={{ margin: 0, width: 150 }}>
          <label className="arc-label" htmlFor="airtime-amount">Other amount</label>
          <input id="airtime-amount" className="arc-input" inputMode="decimal"
            value={amount} onChange={(e) => setAmount(e.target.value.trim())}
            aria-invalid={amount !== '' && !valid} />
          <div className="arc-hint">R{AIRTIME_MIN} to R{AIRTIME_MAX.toLocaleString('en-ZA')}</div>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={!valid || leaving}>
            {leaving ? 'Opening PayFast…' : 'Pay with PayFast'}
          </button>
        </div>
      </div>
      <p className="arc-hint" style={{ margin: '10px 0 0' }}>
        The balance updates when PayFast confirms the payment.
      </p>
    </form>
  );
}

function statusOf(p: Payment): { tone: 'ok' | 'warn' | 'danger' | 'muted'; label: string } {
  switch (p.status) {
    case 'complete': return { tone: 'ok', label: 'paid' };
    case 'failed': return { tone: 'danger', label: 'failed' };
    case 'cancelled': return { tone: 'muted', label: 'cancelled' };
    default: return isAwaiting(p)
      ? { tone: 'warn', label: 'waiting for PayFast' }
      : { tone: 'muted', label: 'not completed' };
  }
}

/** The payments, or one receipt below them: /my/billing/payments/R00012. */
export function PaymentsTab({ tenantId }: { tenantId: string | null }) {
  const route = useSubroute();
  const open = route.rest[0] ?? null;
  return open
    ? <ReceiptView tenantId={tenantId} receiptNumber={open} onBack={() => route.go([])} />
    : <PaymentsList tenantId={tenantId} onOpen={(receipt) => route.go([receipt])} />;
}

function PaymentsList({ tenantId, onOpen }: {
  tenantId: string | null;
  onOpen: (receiptNumber: string) => void;
}) {
  const payments = usePayments(tenantId);
  useBalanceFollowsPayments(tenantId, payments.data?.data);
  const tz = 'Africa/Johannesburg';

  if (payments.error) return <ErrorBanner error={payments.error} />;

  return (
    <div className="arc-card">
      <div className="arc-card-head">
        <h2 className="arc-h2">Payments</h2>
      </div>
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr>
              <th>Date</th><th>Receipt</th><th>For</th><th>Status</th><th>Reference</th>
              <th className="arc-td-right">Amount</th>
            </tr>
          </thead>
          {payments.data === undefined ? <TableSkeleton cols={6} /> : (
            <tbody>
              {payments.data?.data.map((p) => {
                const s = statusOf(p);
                const receipt = p.receipt_number;
                return (
                  <tr key={p.id}>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(p.created_at, tz)}
                    </td>
                    <td>
                      {receipt
                        ? (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-mono"
                            onClick={() => onOpen(receipt)}>
                            {receipt}
                          </button>
                        )
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td>
                      {p.purpose === 'invoice' ? 'Invoices' : 'Airtime'}
                      {p.mode === 'sandbox' && <> <Badge tone="info">test</Badge></>}
                    </td>
                    <td><Badge tone={s.tone}>{s.label}</Badge></td>
                    <td className="arc-mono arc-subtle">{p.provider_payment_id ?? p.reference ?? '—'}</td>
                    <td className="arc-td-right arc-num" style={{ fontWeight: 550 }}>
                      {formatMoney(p.amount, p.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {payments.data !== undefined && payments.data?.data.length === 0 && (
        <Empty icon="billing" title="No payments yet" hint="Airtime bought by card appears here." />
      )}
    </div>
  );
}
